import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { HttpService } from '@app/shared/services/http';
import {
  PractitionerResource,
  OrganizationResource,
} from '@app/shared/models/facade-response.model';
import { catchError, map } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPractitionerResource(
  resource: unknown,
): resource is PractitionerResource {
  return (
    isRecord(resource) &&
    resource['resourceType'] === 'Practitioner' &&
    Array.isArray(resource['name']) &&
    resource['name'].some(
      (n) =>
        isRecord(n) &&
        Array.isArray(n['extension']) &&
        n['extension'].some(
          (ext) =>
            isRecord(ext) &&
            ext['url'] ===
              'http://hl7.org/fhir/StructureDefinition/iso21090-EN-representation' &&
            (ext['valueCode'] === 'IDE' || ext['valueCode'] === 'SYL'),
        ) &&
        typeof n['text'] === 'string',
    )
  );
}

function isOrganizationResource(
  resource: unknown,
): resource is OrganizationResource {
  return (
    isRecord(resource) &&
    resource['resourceType'] === 'Organization' &&
    typeof resource['name'] === 'string'
  );
}

@Injectable({
  providedIn: 'root',
})
export class FhirFacadeService {
  constructor(private httpService: HttpService) {}

  fetchFacadePractitioner(
    hospitalCode: string | null,
    userId: string | null,
  ): Observable<PractitionerResource> {
    if (!hospitalCode || !userId) {
      console.error(
        '[FhirFacadeService] 職員検索に必要な情報を取得できませんでした。',
      );
      return throwError(() => new Error());
    }

    const headers = new HttpHeaders({
      'x-api-key': environment.facadeSettings.apiKey,
    });

    return this.httpService
      .get<unknown>(
        `${environment.facadeSettings.apiUrl}${hospitalCode}/Practitioner?identifier=http://fhir.megaoakcgw.com/identifier/organization/${hospitalCode}/practitioner%7c${userId}`,
        { headers },
      )
      .pipe(
        map((response) => {
          if (
            !isRecord(response) ||
            !Array.isArray(response['entry']) ||
            response['entry'].length === 0 ||
            !isRecord(response['entry'][0]) ||
            !isRecord(response['entry'][0]['resource'])
          ) {
            throw new Error('職員の取得に失敗しました。');
          }

          const resource = response['entry'][0]['resource'];
          if (isPractitionerResource(resource)) {
            return resource as PractitionerResource;
          }
          throw new Error('職員名称を取得できませんでした。');
        }),
        catchError((err) => {
          console.error(`[FhirFacadeService] ${err?.message}`);
          return throwError(() => err);
        }),
      );
  }

  fetchFacadeOrganization(
    hospitalCode: string | null,
  ): Observable<OrganizationResource> {
    if (!hospitalCode) {
      console.error(
        '[FhirFacadeService] 医療機関検索に必要な情報を取得できませんでした。',
      );
      return throwError(() => new Error());
    }

    const headers = new HttpHeaders({
      'x-api-key': environment.facadeSettings.apiKey,
    });

    return this.httpService
      .get<unknown>(
        `${environment.facadeSettings.apiUrl}${hospitalCode}/Organization?identifier=http://fhir.megaoakcgw.com/identifier/organization%7c${hospitalCode}&type=prov`,
        { headers },
      )
      .pipe(
        map((response) => {
          if (
            !isRecord(response) ||
            !Array.isArray(response['entry']) ||
            response['entry'].length === 0 ||
            !isRecord(response['entry'][0]) ||
            !isRecord(response['entry'][0]['resource'])
          ) {
            throw new Error('医療機関の取得に失敗しました。');
          }

          const resource = response['entry'][0]['resource'];
          if (isOrganizationResource(resource)) {
            return resource as OrganizationResource;
          }
          throw new Error('医療機関名称を取得できませんでした。');
        }),
        catchError((err) => {
          console.error(`[FhirFacadeService] ${err?.message}`);
          return throwError(() => err);
        }),
      );
  }
}
